import { useRef, useState } from "react";
import { Volume2 } from "lucide-react";
import { Section, Reveal, Waveform } from "@/components/primitives";
import { languages } from "@/config";
import { speak, playAudioFile, hasVoiceFor, stopAllSpeech, ttsSupported } from "@/lib/speech";

export default function Languages() {
  const [playing, setPlaying] = useState(null);
  const [note, setNote] = useState("");
  const ctrl = useRef(null);

  const play = async (l) => {
    stopAllSpeech();
    ctrl.current?.stop();
    setNote("");
    if (playing === l.code) { setPlaying(null); return; }
    setPlaying(l.code);
    const ok = await playAudioFile(l.audio, { onEnd: () => setPlaying(null) });
    if (ok) return;
    if (!ttsSupported() || !hasVoiceFor(l.lang)) {
      setPlaying(null);
      setNote(`No ${l.label} voice is installed in this browser, so the greeting can't be read aloud here. The recorded file will play once it's added.`);
      return;
    }
    ctrl.current = speak(l.greeting, { lang: l.lang, onEnd: () => setPlaying(null), onError: () => setPlaying(null) });
  };

  return (
    <Section id="languages" tone="charcoal" compact>
      <Reveal className="mx-auto max-w-3xl">
        <div data-testid="languages-card" className="card rounded-2xl p-8 text-center sm:p-10">
          <h2 className="font-serif text-2xl text-cream sm:text-3xl">{languages.headline}</h2>
          <p className="mt-3 text-[15px] text-gray2">{languages.subline}</p>

          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
            {languages.items.map((l) => {
              const on = playing === l.code;
              return (
                <button
                  key={l.code}
                  data-testid={`lang-greeting-${l.code}`}
                  onClick={() => play(l)}
                  aria-pressed={on}
                  className={`flex flex-col items-center gap-2 rounded-xl border px-4 py-6 transition-colors ${
                    on ? "border-oxblood bg-oxblood/20 text-cream" : "border-cream/12 text-cream hover:border-cream/35 hover:bg-cream/5"
                  }`}
                >
                  {on ? <Waveform bars={5} active className="h-4 text-cream" /> : <Volume2 className="h-4 w-4 text-gray2" />}
                  <span className="font-serif text-2xl">{l.native}</span>
                  <span className="text-xs text-gray2">{l.label}</span>
                </button>
              );
            })}
          </div>

          {note && <p data-testid="lang-note" className="mt-5 text-xs text-amber-200/80" role="note">{note}</p>}
        </div>
      </Reveal>
    </Section>
  );
}
